/**
 * SupportChat — Shop-side support messaging panel.
 * Self-contained floating chat widget. Renders a visible trigger button
 * (bottom-right) that opens the support thread with admin.
 * Supports text messages and image/document attachments.
 *
 * Usage: <SupportChat />            — self-managed open/close
 *        <SupportChat open={bool} onClose={fn} />  — externally controlled
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MessageSquare, Send, Paperclip, FileText, CheckCircle2, X } from "lucide-react";

const ALLOWED_TYPES = [
  "image/",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

function isAllowedFile(file) {
  return ALLOWED_TYPES.some((t) =>
    t.endsWith("/") ? file.type.startsWith(t) : file.type === t
  );
}

function isImageDataUrl(str) {
  return typeof str === "string" && str.startsWith("data:image/");
}

function getDocFilename(dataUrl) {
  if (dataUrl.startsWith("data:application/pdf")) return "document.pdf";
  if (dataUrl.includes("wordprocessingml")) return "document.docx";
  if (dataUrl.startsWith("data:application/msword")) return "document.doc";
  return "document";
}

export function SupportChat({ open: externalOpen, onClose: externalOnClose, showLabel = false }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = externalOpen !== undefined;
  const open = isControlled ? externalOpen : internalOpen;
  const handleClose = () => {
    if (isControlled && externalOnClose) externalOnClose();
    else setInternalOpen(false);
  };
  const handleOpen = () => {
    if (!isControlled) setInternalOpen(true);
  };

  const [messages, setMessages] = useState([]);
  const [resolved, setResolved] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [attachmentName, setAttachmentName] = useState("");
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchMessages = async () => {
    try {
      const res = await api.get("/support/messages");
      setMessages(res.data.messages || []);
      setResolved(res.data.resolved || false);
      setStatusMessage(res.data.status_message || null);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (open) {
      fetchMessages();
      // Mark messages as read when chat opens
      api.post("/support/messages/mark-read").catch(() => {});
      setUnreadCount(0);
    }
  }, [open]);

  // Poll unread count when widget is closed
  useEffect(() => {
    if (open) return;
    const fetchUnread = async () => {
      try {
        const res = await api.get("/support/messages/unread-count");
        setUnreadCount(res.data?.unread || 0);
      } catch (e) { /* silent */ }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000); // every 30s
    return () => clearInterval(interval);
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isAllowedFile(file)) {
      toast.error("Supported files: images, PDF, DOCX");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File must be under 5MB");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setAttachment(reader.result);
      setAttachmentName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const sendMessage = async () => {
    if (!input.trim() && !attachment) return;
    setSending(true);
    try {
      const payload = {
        message: input.trim() || "(file attached)",
        attachments: attachment ? [attachment] : [],
      };
      await api.post("/support/messages", payload);
      setInput("");
      setAttachment(null);
      setAttachmentName("");
      fetchMessages();
    } catch (e) { toast.error("Failed to send message"); }
    setSending(false);
  };

  return (
    <>
      {/* Floating trigger button — always visible when chat is closed */}
      {!open && (
        <button
          onClick={handleOpen}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white shadow-lg shadow-[#FF3B30]/20 rounded-full px-4 py-3 transition-all hover:scale-105 active:scale-95"
          aria-label="Open support chat"
          data-testid="support-chat-trigger"
        >
          <MessageSquare className="w-5 h-5" />
          {showLabel && <span className="text-sm font-semibold whitespace-nowrap">Need help? Chat with us</span>}
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[20px] h-5 flex items-center justify-center rounded-full bg-white text-[#FF3B30] text-[11px] font-bold px-1.5 shadow-md animate-pulse">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-[#09090B] border border-zinc-800 rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-[#FF3B30]" />
                <h3 className="text-white font-semibold text-sm">Support</h3>
              </div>
              <button onClick={handleClose} className="text-zinc-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Support hours banner */}
            <div className="px-4 py-2 bg-[#FFB800]/10 border-b border-[#FFB800]/20">
              <p className="text-xs text-[#FFB800]">🕐 Support is available Monday – Friday, 9 AM – 5 PM PT.</p>
              <p className="text-xs text-zinc-400">We'll respond to messages outside those hours on the next business day.</p>
            </div>

            {/* Resolved banner */}
            {resolved && statusMessage && (
              <div className="mx-4 mt-3 flex items-center gap-2 bg-green-900/30 border border-green-800 rounded-lg px-4 py-3">
                <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                <p className="text-green-300 text-sm">{statusMessage}</p>
              </div>
            )}

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[200px]">
              {messages.length === 0 && (
                <div className="text-center text-zinc-500 text-sm py-8">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p>Need help with your application?</p>
                  <p className="text-xs mt-1">Send us a message and we'll get back to you.</p>
                </div>
              )}
              {messages.map((m) => (
                <div key={m.message_id} className={`flex ${m.sender === "shop" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] rounded-xl px-4 py-2.5 ${m.sender === "shop" ? "bg-[#FF3B30] text-white" : "bg-zinc-800 text-zinc-100"}`}>
                    <p className="text-sm whitespace-pre-wrap">{m.message}</p>
                    {m.attachments && m.attachments.length > 0 && (
                      <div className="mt-2 flex gap-2 flex-wrap">
                        {m.attachments.map((att, i) =>
                          isImageDataUrl(att) ? (
                            <img key={i} src={att} alt="attachment" className="w-24 h-24 rounded-lg object-cover border border-white/20" />
                          ) : (
                            <a
                              key={i}
                              href={att}
                              download={getDocFilename(att)}
                              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-black/20 border border-white/10 hover:bg-black/30 transition-colors"
                            >
                              <FileText className="w-4 h-4 flex-shrink-0" />
                              <span className="text-xs truncate max-w-[120px]">{getDocFilename(att)}</span>
                            </a>
                          )
                        )}
                      </div>
                    )}
                    <p className="text-[10px] opacity-60 mt-1">{new Date(m.timestamp).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Input */}
            {!resolved && (
              <div className="border-t border-zinc-800 p-4">
                {attachment && (
                  <div className="mb-2 relative inline-block">
                    {isImageDataUrl(attachment) ? (
                      <img src={attachment} alt="preview" className="w-16 h-16 rounded-lg object-cover border border-zinc-700" />
                    ) : (
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-700 bg-zinc-800">
                        <FileText className="w-4 h-4 text-zinc-300" />
                        <span className="text-xs text-zinc-300 truncate max-w-[140px]">{attachmentName || "document"}</span>
                      </div>
                    )}
                    <button onClick={() => { setAttachment(null); setAttachmentName(""); }} className="absolute -top-1 -right-1 bg-red-500 rounded-full w-4 h-4 flex items-center justify-center">
                      <X className="w-3 h-3 text-white" />
                    </button>
                  </div>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => fileRef.current?.click()}
                    className="p-2.5 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-zinc-400 hover:text-white transition-colors"
                    title="Attach photo or document"
                  >
                    <Paperclip className="w-4 h-4" />
                  </button>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                  <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
                    placeholder="Type a message..."
                    className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#FF3B30] placeholder:text-zinc-500"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={sending || (!input.trim() && !attachment)}
                    className="p-2.5 bg-[#FF3B30] hover:bg-[#FF3B30]/80 text-white rounded-lg disabled:opacity-50 transition-colors"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
